/**
 * Komponen render identitas invoice KANONIS lintas halaman.
 * Data berasal dari field `identity` yang dibangun engine backend
 * (`utils/invoice_identity.py`). Komponen TIDAK melakukan derivasi tanggal
 * / status apa pun — hanya format untuk tampilan.
 */
import { useNavigate } from "react-router-dom";
import { ExternalLink, AlertTriangle, Info } from "lucide-react";
import { fmtDate } from "@/lib/constants";

const REF_SOURCE_LABEL = {
  manual: "Input invoice",
  source_po: "PO transaksi sumber",
  source_doc: "Dokumen transaksi sumber",
};

const MISSING_LABEL = {
  invoice_number: "Nomor invoice",
  invoice_date: "Tanggal invoice",
  due_date: "Jatuh tempo",
  source_link: "Tautan transaksi sumber",
};

export function CompletenessBadges({ identity, size = "sm" }) {
  const c = identity?.completeness;
  if (!c) return null;
  const items = [];
  if (c.needs_verification) items.push({ k: "verif", label: "Perlu Verifikasi", cls: "border-amber-400 bg-amber-50 text-amber-800" });
  if (c.is_incomplete) items.push({ k: "incomplete", label: "Data Belum Lengkap", cls: "border-rose-400 bg-rose-50 text-rose-800" });
  if (!items.length) return null;
  const px = size === "xs" ? "px-1 py-0.5 text-[9px]" : "px-1.5 py-0.5 text-[10px]";
  return (
    <div className="inline-flex items-center gap-1 flex-wrap" data-testid="completeness-badges">
      {items.map((it) => (
        <span key={it.k} className={`${px} font-semibold uppercase tracking-wider border ${it.cls} inline-flex items-center gap-1`} data-testid={`badge-${it.k}`}>
          <AlertTriangle size={9} /> {it.label}
        </span>
      ))}
    </div>
  );
}

/** Baris identitas: nomor invoice (link ke drawer), referensi + sumber, tautan transaksi sumber. */
export function InvoiceIdentityCell({ identity, onOpenDetail }) {
  const nav = useNavigate();
  if (!identity) return <span className="text-slate-300 text-xs">—</span>;
  const openSource = () => {
    if (!identity.source?.available || !identity.source?.link) return;
    nav(identity.source.link);
  };
  const openDetail = () => {
    if (onOpenDetail) onOpenDetail();
    else if (identity.detail_link) nav(identity.detail_link);
  };
  return (
    <div className="min-w-0 space-y-0.5" data-testid={`invoice-identity-${identity.entry_id || identity.entry_key || "x"}`}>
      <div className="flex items-center gap-1.5 flex-wrap">
        {identity.invoice_number ? (
          <button type="button"
            data-testid={`invoice-no-link-${identity.entry_id}`}
            onClick={openDetail}
            title="Buka detail invoice"
            className="font-mono text-xs font-semibold text-[#0B1D3A] hover:text-[#FF5A1F] underline underline-offset-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF5A1F]">
            {identity.invoice_number}
          </button>
        ) : (
          <span className="italic text-[11px] text-slate-400" data-testid={`invoice-no-missing-${identity.entry_id}`}>Nomor invoice belum diisi</span>
        )}
        {identity.txn_kind === "klaim_retensi" && (
          <span className="px-1 py-0.5 text-[9px] font-bold tracking-wider border border-purple-300 bg-purple-50 text-purple-700" data-testid="klaim-retensi-badge">KLAIM RETENSI</span>
        )}
      </div>
      <div className="flex items-center gap-1 text-[10px] text-slate-500">
        <span className="uppercase tracking-wider">Ref</span>
        {identity.external_reference ? (
          <>
            <span className="font-mono text-slate-700" data-testid={`invoice-ref-${identity.entry_id}`}>{identity.external_reference}</span>
            <span className="text-slate-400">· {REF_SOURCE_LABEL[identity.reference_source] || identity.reference_source}</span>
          </>
        ) : (
          <span className="italic text-slate-400" data-testid={`invoice-ref-missing-${identity.entry_id}`}>Referensi eksternal belum diisi</span>
        )}
      </div>
      <div className="flex items-center gap-2 text-[10px]">
        {identity.source?.available ? (
          <button type="button"
            data-testid={`btn-open-source-${identity.entry_id}`}
            onClick={openSource}
            title={`Buka ${identity.source.label}`}
            className="inline-flex items-center gap-1 text-sky-700 hover:text-sky-900 underline underline-offset-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF5A1F]">
            <ExternalLink size={10} /> Buka Transaksi Sumber
          </button>
        ) : (
          <span className="italic text-slate-400 inline-flex items-center gap-1" data-testid={`source-missing-${identity.entry_id}`}>
            <Info size={9} /> Tautan sumber belum tersedia
          </span>
        )}
      </div>
      <CompletenessBadges identity={identity} />
    </div>
  );
}

/** Blok ringkas: dipakai di drawer / monitoring / drill-down dashboard. */
export function InvoiceIdentityPanel({ identity, onComplete }) {
  if (!identity) return null;
  const c = identity.completeness || {};
  const rows = [
    ["Nomor Invoice", identity.invoice_number || <span className="italic text-slate-400">Nomor invoice belum diisi</span>, "no"],
    ["Referensi Eksternal", identity.external_reference
      ? <>{identity.external_reference} <span className="text-[10px] text-slate-500">· {REF_SOURCE_LABEL[identity.reference_source] || identity.reference_source}</span></>
      : <span className="italic text-slate-400">Referensi eksternal belum diisi</span>, "ref"],
    ["Proyek / WO", identity.project_name || "—", "proj"],
    ["Client", identity.client || "—", "client"],
    ["Tanggal Invoice", identity.invoice_date ? fmtDate(identity.invoice_date) : <span className="italic text-slate-400">Belum diisi</span>, "idate"],
    ["Tanggal Jatuh Tempo", identity.due_date ? fmtDate(identity.due_date) : <span className="italic text-slate-400">Jatuh tempo belum diisi</span>, "due"],
    ["Jenis Transaksi Sumber", identity.source?.label || "—", "skind"],
    ["ID Transaksi Sumber", identity.source?.entry_id || <span className="italic text-slate-400">Belum tertaut</span>, "sid"],
    ["Status Verifikasi", identity.verification_status?.label || "—", "vst"],
    ...(identity.verification_status?.reason ? [["Alasan Perlu Verifikasi", identity.verification_status.reason, "vreason"]] : []),
    ["Penanggung Jawab", identity.pic_name || <span className="italic text-slate-400">Belum ditetapkan</span>, "pic"],
    ["Terakhir Diubah", identity.last_updated_at ? fmtDate(identity.last_updated_at) : "—", "upd"],
    ["Umur Data", identity.age_days != null ? `${identity.age_days} hari` : "—", "age"],
  ];
  return (
    <div className="border border-slate-200 bg-white" data-testid="invoice-identity-panel">
      <div className="px-3 py-2 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
        <div className="text-xs font-semibold text-[#0B1D3A] uppercase tracking-wider">Identitas Invoice</div>
        <div className="flex items-center gap-2">
          <CompletenessBadges identity={identity} />
          {onComplete && (c.is_incomplete || c.needs_verification) && (
            <button type="button" data-testid="btn-complete-identity"
              onClick={onComplete}
              className="px-2 py-1 text-[11px] font-semibold uppercase tracking-wider bg-[#FF5A1F] hover:bg-[#e24e1a] text-white">
              Lengkapi Data
            </button>
          )}
        </div>
      </div>
      <div className="divide-y divide-slate-100 text-xs">
        {rows.map(([label, val, k]) => (
          <div key={k} className="flex items-start justify-between gap-3 px-3 py-1.5">
            <span className="text-slate-500 shrink-0">{label}</span>
            <span className="text-right text-[#0B1D3A] font-medium break-words max-w-[60%]" data-testid={`identity-field-${k}`}>{val}</span>
          </div>
        ))}
        {identity.source?.available && (
          <div className="px-3 py-2 bg-sky-50">
            <a href={identity.source.link} className="inline-flex items-center gap-1 text-[11px] text-sky-700 hover:text-sky-900 underline" data-testid="identity-source-link">
              <ExternalLink size={11} /> Buka Transaksi Sumber
            </a>
          </div>
        )}
        {c.is_incomplete && (
          <div className="px-3 py-2 bg-rose-50 text-[11px] text-rose-800" data-testid="identity-missing-list">
            <span className="font-semibold">Belum lengkap:</span>{" "}
            {(c.missing_fields || []).map((m) => MISSING_LABEL[m] || m).join(", ")}
          </div>
        )}
      </div>
    </div>
  );
}
